/**
 * Keeps a pressed submit button a Busy button until the result of its action is on the page.
 *
 * Turbo's own `disabled` mode enables the button again at `requestFinished`, which runs once the
 * response headers arrive, before the body is read and before anything renders. Over a slow
 * connection the button looks ready for seconds while the next page still downloads, and a second
 * press sends the same save again. This submitter hands the button back only when
 * `submission-phases.ts` releases the submission.
 *
 * The button is disabled and carries `data-submit-busy`, which only `Components/buttons.css` acts on.
 * Turbo clears the form's `aria-busy` at `requestFinished` too, so it is set again right after
 * `turbo:submit-end` and cleared at the same release as the button. A form that submits itself passes Turbo no button
 * and is left to Turbo's progress bar.
 *
 * `turbo:before-cache` releases every held button, so a cached page, such as a search the member
 * returns to with Back, never shows a Busy button.
 */

import type { SubmitterConfig } from "@hotwired/turbo";
import { installSubmissionPhases, type SubmissionPhases } from "./submission-phases";
import type { FormSubmissionLike } from "./turbo-submit-event";

const BUSY_MARK = "data-submit-busy";

type Submitter = HTMLButtonElement | HTMLInputElement;

function isSubmitter(element: unknown): element is Submitter {
   return element instanceof HTMLButtonElement || element instanceof HTMLInputElement;
}

function release(submission: FormSubmissionLike): void {
   const { submitter, formElement } = submission;
   if (!isSubmitter(submitter)) {
      return;
   }
   submitter.disabled = false;
   submitter.removeAttribute(BUSY_MARK);
   if (formElement instanceof HTMLFormElement) {
      formElement.removeAttribute("aria-busy");
   }
}

const busySubmitter: SubmitterConfig = {
   beforeSubmit(submitter) {
      if (isSubmitter(submitter)) {
         submitter.disabled = true;
         submitter.setAttribute(BUSY_MARK, "");
      }
   },
   // The release enables the button, once the result is on the page.
   afterSubmit() {},
};

/**
 * Makes `forms.submitter` the Busy button submitter. Returns an uninstall function (tests) that
 * restores the previous submitter.
 */
export function installBusySubmitButton(forms: { submitter: SubmitterConfig | "disabled" | "aria-disabled" }): () => void {
   const previous = forms.submitter;
   const phases: SubmissionPhases = installSubmissionPhases({
      scope: "every-update",
      // A turbo-frame controller clears the form's `aria-busy` once more right after `turbo:submit-end`.
      onSubmitEnd: (submission) => queueMicrotask(() => hold(submission)),
      onRelease: release,
   });
   function hold(submission: FormSubmissionLike): void {
      const { submitter, formElement } = submission;
      if (phases.follows(submission) && isSubmitter(submitter) && formElement instanceof HTMLFormElement) {
         formElement.setAttribute("aria-busy", "true");
      }
   }

   forms.submitter = busySubmitter;
   document.addEventListener("turbo:before-cache", phases.releaseAll);

   return () => {
      document.removeEventListener("turbo:before-cache", phases.releaseAll);
      forms.submitter = previous;
      phases.uninstall();
   };
}
